import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/** One original blob that still needs work, straight from the container listing. */
export interface DerivativeScanItem {
  name: string;
  sizeBytes: number | null;
  contentType: string | null;
  lastModified: string | null;
  /**
   * Derivative names absent from the container. Listing-only knowledge — a source narrower
   * than a width legitimately never gets that copy, which generating settles for good.
   */
  missingWidths: number[];
  needsContentType: boolean;
}

export interface DerivativeScanResult {
  totalOriginals: number;
  totalComplete: number;
  /** More blobs need work than fit in one response; re-scan after generating to see the rest. */
  truncated: boolean;
  items: DerivativeScanItem[];
}

export type BackfillStatus = 'generated' | 'alreadyComplete' | 'tooSmall' | 'notResizable' | 'notFound' | 'failed';

export interface DerivativeBackfillResult {
  name: string;
  status: BackfillStatus;
  widthsWritten: number[];
  contentTypeFixed: boolean;
  error: string | null;
}

/**
 * Image derivative console — api/admin/image-derivatives on the Inventory API (platform staff
 * only). Images uploaded before the API generated resized copies on upload have none, so the POS
 * grid falls back to full-size originals for them; scan finds those, generate fixes them.
 */
@Injectable({ providedIn: 'root' })
export class ImageDerivativesService {
  /** environment.inventoryApiUrl already ends in /api. */
  private readonly baseUrl = environment.inventoryApiUrl + '/admin/image-derivatives';

  /** The API refuses larger batches; the page chunks bigger runs. */
  readonly maxBatch = 20;

  constructor(private http: HttpClient) {}

  scan(container: string): Observable<DerivativeScanResult> {
    const params = new HttpParams().set('container', container);
    return this.http.get<DerivativeScanResult>(`${this.baseUrl}/scan`, { params });
  }

  generate(container: string, names: string[]): Observable<DerivativeBackfillResult[]> {
    return this.http.post<DerivativeBackfillResult[]>(`${this.baseUrl}/generate`, { container, names });
  }
}
